// Import apiService
import apiService from '@/services/api'

// State
const state = {
  maturityLevels: [],
  riskLevels: [],
  loading: {
    maturityLevels: false,
    riskLevels: false
  },
  error: {
    maturityLevels: null,
    riskLevels: null
  }
}

// Getters
const getters = {
  getMaturityLevelById: (state) => (id) => {
    return state.maturityLevels.find(level => (level.id ?? level.level) === id)
  },
  getRiskLevelById: (state) => (id) => {
    return state.riskLevels.find(level => level.id === id)
  },
  getMaturityName: (state) => (id) => {
    const level = state.maturityLevels.find(level => (level.id ?? level.level) === id)
    return level ? level.name : 'Unknown'
  },
  getMaturityDescription: (state) => (id) => {
    const level = state.maturityLevels.find(level => (level.id ?? level.level) === id)
    return level ? level.description : ''
  },
  getRiskName: (state) => (id) => {
    const level = state.riskLevels.find(level => level.id === id)
    return level ? level.name : 'Unknown'
  },
  getRiskDescription: (state) => (id) => {
    const level = state.riskLevels.find(level => level.id === id)
    return level ? level.description : ''
  },
  isLoading: (state) => (type) => {
    return state.loading[type]
  },
  getError: (state) => (type) => {
    return state.error[type]
  }
}

// Mutations
const mutations = {
  setMaturityLevels(state, levels) {
    state.maturityLevels = levels
  },
  setRiskLevels(state, levels) {
    state.riskLevels = levels
  },
  updateMaturityLevel(state, update) {
    const index = state.maturityLevels.findIndex(level => level.id === update.id)
    if (index !== -1) {
      state.maturityLevels[index] = {
        ...state.maturityLevels[index],
        ...update
      }
    }
  },
  updateRiskLevel(state, update) {
    const index = state.riskLevels.findIndex(level => level.id === update.id)
    if (index !== -1) {
      state.riskLevels[index] = {
        ...state.riskLevels[index],
        ...update
      }
    }
  },
  removeMaturityLevel(state, levelId) {
    state.maturityLevels = state.maturityLevels.filter(level => (level.id ?? level.level) !== levelId)
  },
  removeRiskLevel(state, levelId) {
    state.riskLevels = state.riskLevels.filter(level => level.id !== levelId)
  },
  setLoading(state, { type, isLoading }) {
    state.loading[type] = isLoading
  },
  setError(state, { type, error }) {
    state.error[type] = error
  }
}

// Actions
const actions = {
  async fetchMaturityLevels({ commit }, { apiService: svc } = {}) {
    const api = svc || apiService
    commit('setLoading', { type: 'maturityLevels', isLoading: true })
    commit('setError', { type: 'maturityLevels', error: null })
    
    try {
      const levels = await api.getMaturityLevels()
      commit('setMaturityLevels', levels)
    } catch (error) {
      commit('setError', { 
        type: 'maturityLevels', 
        error: error.message || 'Failed to fetch maturity levels' 
      })
      throw error
    } finally {
      commit('setLoading', { type: 'maturityLevels', isLoading: false })
    }
  },

  async fetchRiskLevels({ commit }, { apiService: svc } = {}) {
    const api = svc || apiService
    commit('setLoading', { type: 'riskLevels', isLoading: true })
    commit('setError', { type: 'riskLevels', error: null })
    
    try {
      // Prefer confidence-levels endpoint; fall back to risk-levels for compatibility
      const levels = (await api.getConfidenceLevels?.()) || (await api.getRiskLevels())
      commit('setRiskLevels', levels)
    } catch (error) {
      commit('setError', { 
        type: 'riskLevels', 
        error: error.message || 'Failed to fetch risk levels' 
      })
      throw error
    } finally {
      commit('setLoading', { type: 'riskLevels', isLoading: false })
    }
  },

  async updateMaturityLevel({ commit }, { apiService: svc, update }) {
    const api = svc || apiService
    try {
      const response = await api.updateMaturityLevel(update.id, 
        // Only send the fields that are being updated
        Object.fromEntries(
          Object.entries(update).filter(([key]) => key !== 'id')
        )
      )
      commit('updateMaturityLevel', response)
      return response
    } catch (error) {
      commit('setError', { 
        type: 'maturityLevels', 
        error: error.message || 'Failed to update maturity level' 
      })
      throw error
    }
  },

  async updateRiskLevel({ commit }, { apiService: svc, update }) {
    const api = svc || apiService
    try {
      // Try confidence-levels endpoint first
      const updater = api.updateConfidenceLevel ? api.updateConfidenceLevel.bind(api) : api.updateRiskLevel.bind(api)
      const response = await updater(update.id,
        // Only send the fields that are being updated
        Object.fromEntries(
          Object.entries(update).filter(([key]) => key !== 'id')
        )
      )
      commit('updateRiskLevel', response)
      return response
    } catch (error) {
      commit('setError', { 
        type: 'riskLevels', 
        error: error.message || 'Failed to update risk level' 
      })
      throw error
    }
  },

  async deleteMaturityLevel({ commit }, { apiService: svc, id }) {
    const api = svc || apiService
    try {
      await api.deleteMaturityLevel(id)
      commit('removeMaturityLevel', id)
    } catch (error) {
      commit('setError', {
        type: 'maturityLevels',
        error: error.message || 'Failed to delete maturity level'
      })
      throw error
    }
  },

  async deleteRiskLevel({ commit }, { apiService: svc, id }) {
    const api = svc || apiService
    try {
      // Try confidence-levels endpoint first
      const deleter = api.deleteConfidenceLevel ? api.deleteConfidenceLevel.bind(api) : api.deleteRiskLevel.bind(api)
      await deleter(id)
      commit('removeRiskLevel', id)
    } catch (error) {
      commit('setError', {
        type: 'riskLevels',
        error: error.message || 'Failed to delete confidence level'
      })
      throw error
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} 